import { useState } from "react";

import {
  DocumentPaymentScheduleStatusEnum,
  PublicDocumentPaymentScheduleQuery,
} from "@/__generated__/graphql";
import { Values } from "@/constants";
import { Utils } from "@/utils";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";
import { Input } from "@/components/ui/input";

/** Instalments come from ``PUBLIC_DOCUMENT_PAYMENT_SCHEDULE_QUERY``, merged onto the page's node. */
export type PublicDocumentPaymentSchedule = Extract<
  NonNullable<PublicDocumentPaymentScheduleQuery["node"]>,
  { __typename?: "PublicDocumentObject" }
>["paymentSchedule"];

type PaymentScheduleItem = PublicDocumentPaymentSchedule[number];

// The public page talks to GraphQL over plain ``fetch`` — it has no Clerk
// session and so no Apollo client — which is why this mutation is spelled out
// here rather than pulled from ``Mutations``.
const MARK_PAID_MUTATION_STRING = `
  mutation MarkPaymentScheduleItemPaid($input: MarkPaymentScheduleItemPaidInput!) {
    markPaymentScheduleItemPaid(input: $input) {
      errors {
        notFoundError { message }
        genericErrors { message }
      }
      data { id identifier status dateClaimed claimedReference }
    }
  }
`;

const STATUS_CHIPS: Record<
  DocumentPaymentScheduleStatusEnum,
  { label: string; variant: "secondary" | "destructive" }
> = {
  [DocumentPaymentScheduleStatusEnum.Pending]: {
    label: "Due",
    variant: "secondary",
  },
  [DocumentPaymentScheduleStatusEnum.AwaitingConfirmation]: {
    label: "Awaiting confirmation",
    variant: "secondary",
  },
  [DocumentPaymentScheduleStatusEnum.Paid]: {
    label: "Paid",
    variant: "secondary",
  },
  [DocumentPaymentScheduleStatusEnum.Cancelled]: {
    label: "Cancelled",
    variant: "destructive",
  },
};

function PaymentScheduleRow({
  item,
  currency,
  isSelected,
  canPay,
  onSelect,
  onReportPaid,
  isReporting,
}: {
  item: PaymentScheduleItem;
  currency: string;
  isSelected: boolean;
  canPay: boolean;
  onSelect: () => void;
  onReportPaid: (reference: string) => void;
  isReporting: boolean;
}) {
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [reference, setReference] = useState("");

  const status = item.status ?? DocumentPaymentScheduleStatusEnum.Pending;
  const chip = STATUS_CHIPS[status];
  const isOpen = status === DocumentPaymentScheduleStatusEnum.Pending;

  return (
    <div
      className={cn(
        "flex flex-col gap-2 p-3 rounded-sm [border-width:1px] [border-style:solid] border-[initial]",
        isSelected ? "border-ring" : "border-border",
        isSelected ? "bg-accent" : "bg-background",
      )}
    >
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <Typography level="title-sm" className="truncate">
            {item.name || "Instalment"}
            {item.percent != null ? ` · ${Number(item.percent)}%` : ""}
          </Typography>
          <Typography level="body-xs" className="text-muted-foreground/80">
            {item.dateDue
              ? `Due ${Utils.fDate(new Date(item.dateDue))}`
              : "Due on milestone"}
          </Typography>
        </div>
        <Typography level="title-sm">
          {Utils.fCurrency(Number(item.amount ?? 0))} {currency}
        </Typography>
        <Badge variant={chip.variant}>{chip.label}</Badge>
      </div>

      {isOpen && (
        <div className="flex gap-2 flex-wrap">
          {canPay && (
            <Button
              size="sm"
              variant={isSelected ? "default" : "outline"}
              onClick={onSelect}
            >
              {isSelected ? "Paying this" : "Pay by card"}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsReportOpen((open) => !open)}
          >
            I&apos;ve already paid this
          </Button>
        </div>
      )}

      {isOpen && isReportOpen && (
        <div className="flex gap-2 items-center">
          <Input
            placeholder="Payment reference (optional)"
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            className="h-8"
          />
          <Button
            size="sm"
            onClick={() => onReportPaid(reference)}
            disabled={isReporting}
          >
            {isReporting && <Spinner />}Report payment
          </Button>
        </div>
      )}

      {status === DocumentPaymentScheduleStatusEnum.AwaitingConfirmation && (
        <Typography level="body-xs" className="text-muted-foreground/80">
          Reported as paid
          {item.claimedReference ? ` (${item.claimedReference})` : ""}. Waiting
          for confirmation from the sender.
        </Typography>
      )}
    </div>
  );
}

export function PublicDocumentViewPaymentSchedule({
  schedule,
  currency,
  canPay,
  selectedItemIdentifier,
  onSelectItem,
  onReported,
}: {
  schedule: PaymentScheduleItem[];
  currency: string;
  canPay: boolean;
  selectedItemIdentifier: string | null;
  onSelectItem: (identifier: string) => void;
  onReported: () => void;
}) {
  const [reportingIdentifier, setReportingIdentifier] = useState<string | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const handleReportPaid = async (
    item: PaymentScheduleItem,
    reference: string,
  ) => {
    setReportingIdentifier(item.identifier ?? null);
    setError(null);
    try {
      const response = await fetch(Values.URLS["GRAPHQL"], {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: MARK_PAID_MUTATION_STRING,
          variables: {
            input: {
              scheduleItemIdentifier: item.identifier,
              reference: reference || null,
            },
          },
        }),
      });
      const json = await response.json();
      const errors = json?.data?.markPaymentScheduleItemPaid?.errors;
      const message =
        errors?.notFoundError?.message ?? errors?.genericErrors?.[0]?.message;
      if (message) {
        setError(message);
        return;
      }
      onReported();
    } catch {
      setError("Could not report this payment.");
    } finally {
      setReportingIdentifier(null);
    }
  };

  const outstanding = schedule
    .filter((item) => item.status !== DocumentPaymentScheduleStatusEnum.Paid)
    .reduce((sum, item) => sum + Number(item.amount ?? 0), 0);

  return (
    <div className="flex flex-col gap-3 w-full">
      <div>
        <Typography level="body-sm" className="text-muted-foreground">
          Payment schedule
        </Typography>
        <Typography level="body-xs" className="text-muted-foreground/80">
          {Utils.fCurrency(outstanding)} {currency} still outstanding across{" "}
          {schedule.length} instalment{schedule.length === 1 ? "" : "s"}
        </Typography>
      </div>

      {schedule.map((item) => (
        <PaymentScheduleRow
          key={item.identifier}
          item={item}
          currency={currency}
          canPay={canPay}
          isSelected={selectedItemIdentifier === item.identifier}
          isReporting={reportingIdentifier === item.identifier}
          onSelect={() => onSelectItem(item.identifier as string)}
          onReportPaid={(reference) => handleReportPaid(item, reference)}
        />
      ))}

      {error && (
        <Typography level="body-sm" className="text-destructive">
          {error}
        </Typography>
      )}
    </div>
  );
}
